import { Account, AwayMode, Energy, Spinner } from '@mgh-app/component-library';
import { RoomButton } from 'components/RoomButton';
import { SettingsButton } from 'components/SettingsButton';
import { MainLayoutContext } from 'layouts/MainLayout';
import { EnergyUse } from 'models/Thing';
import { useAppNavigation } from 'navigation/useAppNavigation';
import { useContext, useEffect } from 'react';
import { useGetMyRoomsQuery } from 'services/RoomService';
import { selectEnergyUse } from 'services/ThingsService';
import { useAppSelector } from 'store';
import {
  display,
  flexDirection,
  flexWrap,
  gap,
  gridAutoFlow,
  gridAutoRows,
  gridTemplateColumns,
  gridTemplateRows,
  justifyContent,
  margin,
  overflow,
  tailwindClassNameConvertNegative,
  twCls,
} from 'style';

const isEnergyDataAvailable = (data: EnergyUse | undefined) => {
  if (!data) {
    return false;
  }

  return !(
    (data.currentUse === 0 || isNaN(data.currentUse)) &&
    data.weeklyUse.every((d) => d === 0 || isNaN(d)) &&
    data.monthlyUse.every((d) => d === 0 || isNaN(d))
  );
};

export const Home = () => {
  const { navigateRoom, navigateEnergy, navigateAccount, navigateHomeSettings } =
    useAppNavigation();
  const { setTitle, showNavigationButtons, showUserProfile } = useContext(MainLayoutContext);
  const energyUse = useAppSelector((state) => selectEnergyUse(state));
  const { data: rooms, isLoading } = useGetMyRoomsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    setTitle('');
    showUserProfile(true);
    showNavigationButtons(false);
  }, [setTitle, showNavigationButtons, showUserProfile]);

  return (
    <div className={twCls(display('grid'), gridTemplateRows('grid-rows-fc-min'), margin('mt-17'))}>
      <div
        className={twCls(
          overflow('overflow-auto'),
          margin(
            tailwindClassNameConvertNegative('-ml-2'),
            tailwindClassNameConvertNegative('-mr-2'),
          ),
          display('grid'),
          gap('gap-2'),
          justifyContent('justify-center'),
          gridAutoFlow('grid-flow-row'),
          gridAutoRows('auto-rows-min'),
          gridTemplateColumns('grid-cols-responsive-buttons'),
        )}
      >
        {isLoading && <Spinner />}
        {!isLoading &&
          rooms &&
          rooms.map((room) => (
            <RoomButton
              onClick={() => navigateRoom(room.id)}
              key={room.id}
              name={room.nickName}
              iconUrl={room.image}
            />
          ))}
      </div>
      <div
        className={twCls(
          margin('mt-7'),
          display('flex'),
          flexDirection('flex-row'),
          flexWrap('flex-wrap'),
          justifyContent('justify-center'),
        )}
      >
        {isEnergyDataAvailable(energyUse) && (
          <SettingsButton title="Energy Use" onClick={navigateEnergy}>
            <Energy />
          </SettingsButton>
        )}
        <SettingsButton title="Account" onClick={navigateAccount}>
          <Account />
        </SettingsButton>
        <SettingsButton title="Away Mode" onClick={navigateHomeSettings}>
          <AwayMode className={twCls(margin('ml-1'))} />
        </SettingsButton>
      </div>
    </div>
  );
};
